import { faCirclePlus, faPenToSquare } from "@fortawesome/pro-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { alpha, Button, TextField } from "@mui/material"
import { translate } from "app/language/service"
import { NOT_SAVED_ID } from "app/model"
import { DEFAULT_RADIUS_VALUE, TYPE_COORDINATE } from "coordinate/model"
import { refreshTempCoordinateMap, setTempCoordinateFeature } from "form/map/tools/geo-targets-coordinate"
import { GEO_TARGETS_FORM_TABS, IGeoTargetsForm } from "form/model"
import {
  isValidLatitude as isValidLatitudeFn,
  isValidLongitude as isValidLongitudeFn,
  isValidRadius as isValidRadiusFn,
  setGeoTargetsForm,
  stopUpdatingGeoTargetsForm
} from "form/tools/geo-targets"
import { addCoordinate, getCoordinateRadiusInMeter, updateCoordinate } from "form/tools/geo-targets-coordinates"
import React from "react"
import { FlexBox } from "ui/components/FlexBox"
import { FieldDistance } from "./FieldDistance"

interface IGeoTargetsCoordinateFormProps {
  geoTargetsForm: IGeoTargetsForm
}

export const GeoTargetsCoordinateForm = React.memo(
  ({ geoTargetsForm }: IGeoTargetsCoordinateFormProps): JSX.Element => {
    const { isCreation, latitude, longitude, name, radius, toUpdateEntityId } = geoTargetsForm
    const isValidName = name !== ""
    const isValidLatitude = isValidLatitudeFn(latitude)
    const isValidLongitude = isValidLongitudeFn(longitude)
    const isValidRadius = isValidRadiusFn(radius)
    const hasFormError: boolean = !isValidName || !isValidLatitude || !isValidLongitude || !isValidRadius
    const setLatitude = (newLatitude: string | null) => {
      const isValidNewLatitude = isValidLatitudeFn(newLatitude)
      if (newLatitude === null || !isValidNewLatitude || !isValidLongitude) {
        setTempCoordinateFeature(null)
      } else {
        setTempCoordinateFeature({
          x: Number(longitude),
          y: Number(newLatitude)
        })
      }
      setGeoTargetsForm({ latitude: newLatitude })
    }
    const setLongitude = (newLongitude: string | null) => {
      const isValidNewLongitude = isValidLongitudeFn(newLongitude)
      if (newLongitude === null || !isValidLatitude || !isValidNewLongitude) {
        setTempCoordinateFeature(null)
      } else {
        setTempCoordinateFeature({
          x: Number(newLongitude),
          y: Number(latitude)
        })
      }
      setGeoTargetsForm({ longitude: newLongitude })
    }
    const setRadius = (newRadius: string | null) => {
      setGeoTargetsForm({ radius: newRadius })
      refreshTempCoordinateMap()
    }
    const resetCoordinateFormInState = () => {
      setGeoTargetsForm({
        isCreation: true,
        latitude: null,
        longitude: null,
        radius: DEFAULT_RADIUS_VALUE,
        name: "",
        activeTab: GEO_TARGETS_FORM_TABS.COORDINATE
      })
    }
    const submit = async () => {
      if (hasFormError) {
        return
      }
      if (isCreation) {
        await addCoordinate({
          id: NOT_SAVED_ID,
          latitude: Number(latitude) || 0,
          longitude: Number(longitude) || 0,
          name,
          radius: getCoordinateRadiusInMeter(),
          is_deleted: false,
          type: TYPE_COORDINATE,
          campaign_id: toUpdateEntityId,
          created_at: null,
          updated_at: null
        })
      } else {
        updateCoordinate({
          id: toUpdateEntityId,
          latitude: Number(latitude) || 0,
          longitude: Number(longitude) || 0,
          name,
          radius: getCoordinateRadiusInMeter(),
          is_deleted: false,
          type: TYPE_COORDINATE,
          campaign_id: toUpdateEntityId,
          created_at: null,
          updated_at: null
        })
      }
      resetCoordinateFormInState()
    }
    const onKeyUp = (event: React.KeyboardEvent<HTMLDivElement>) => {
      event.stopPropagation()
      event.preventDefault()
      if (event.key === "Enter" && !hasFormError) {
        submit()
      }
    }
    return (
      <FlexBox
        vertical
        gap
        fullWidth
        sx={{
          "marginLeft": ".5rem",
          "@keyframes borderHighlight": {
            "0%": {
              boxShadow: theme => `0px 0px 0px 1px ${alpha(theme.palette.background.default as string, 1)}`,
              borderRadius: ".25rem"
            },
            "50%": {
              boxShadow: theme => `0px 0px 0px 1px ${alpha(theme.palette.primary.main as string, 1)}`,
              borderRadius: ".25rem"
            },
            "100%": {
              boxShadow: theme => `0px 0px 0px 1px ${alpha(theme.palette.background.default as string, 1)}`,
              borderRadius: ".25rem"
            }
          },
          "& .MuiFormControl-root": {
            animation: isCreation ? undefined : "borderHighlight 1000ms",
            animationIterationCount: 2
          },
          "& > *": { width: "100%" }
        }}
        className={isCreation ? undefined : "form-geo-target-update-animation"}>
        <TextField
          id="geo-target-coordinates-name"
          label={translate("label.name")}
          value={name}
          size="small"
          required
          onKeyUp={event => onKeyUp(event)}
          onChange={event => setGeoTargetsForm({ name: event.target.value })}
        />
        <TextField
          id="geo-target-coordinates-latitude"
          label={translate("label.latitude")}
          value={latitude === null ? "" : latitude}
          type="number"
          size="small"
          error={latitude !== null && !isValidLatitude}
          helperText={
            latitude !== null && !isValidLatitude ? translate("campaign.geo-targets.form.error.latitude") : undefined
          }
          required
          onKeyUp={event => onKeyUp(event)}
          onChange={event => setLatitude(event.target.value === "" ? null : event.target.value)}
        />
        <TextField
          id="geo-target-coordinates-longitude"
          label={translate("label.longitude")}
          value={longitude === null ? "" : longitude}
          type="number"
          size="small"
          required
          error={longitude !== null && !isValidLongitude}
          helperText={
            longitude !== null && !isValidLongitude ? translate("campaign.geo-targets.form.error.longitude") : undefined
          }
          onKeyUp={event => onKeyUp(event)}
          onChange={event => setLongitude(event.target.value === "" ? null : event.target.value)}
        />
        <FieldDistance
          id="geo-target-coordinates-radius"
          label={translate("label.radius")}
          value={radius === null ? "" : radius}
          required
          error={radius !== null && !isValidRadius ? translate("campaign.geo-targets.form.error.radius") : ""}
          onEnterKey={() => submit()}
          sx={{ "width": "100%", "& > *": { width: "100%" } }}
          onChange={newRadius => setRadius(newRadius)}
        />
        <FlexBox jc="end" fullWidth gap>
          {isCreation && isValidLatitude && isValidLongitude && (
            <Button
              id="geo-target-coordinates-cancel-button"
              variant="outlined"
              size="small"
              onClick={() => {
                setTempCoordinateFeature(null)
                resetCoordinateFormInState()
              }}>
              {translate("button.cancel")}
            </Button>
          )}
          {!isCreation && (
            <Button
              id="geo-target-coordinates-cancel-button"
              variant="outlined"
              size="small"
              onClick={() => stopUpdatingGeoTargetsForm()}>
              {translate("button.cancel")}
            </Button>
          )}
          <Button
            id="geo-target-coordinates-save-button"
            disabled={hasFormError}
            variant="outlined"
            size="small"
            onClick={() => submit()}
            startIcon={<FontAwesomeIcon icon={isCreation ? faCirclePlus : faPenToSquare} />}>
            {translate(isCreation ? "button.add" : "button.update")}
          </Button>
        </FlexBox>
      </FlexBox>
    )
  }
)
